'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageWhyPropTradingSchema } from '@/sanity/pages/PageWhyPropTradingTemplate';
import Image from 'next/image';
import dotsBg from '@/public/home/dots-bg-dark.png';
import { CTAButton } from '@/shadcn/ui/button';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function MeansForYouSection({
  data,
}: {
  data: PageWhyPropTradingSchema;
}) {
  if (!data?.meansForYouTitle && !data?.meansForYouStats?.length) return null;

  return (
    <section className="relative w-full flex flex-col items-center bg-[#04271d] overflow-clip py-[8rem] lg:py-[18rem] px-[2rem] lg:px-[6rem]">
      <div
        className="absolute inset-0 opacity-40 pointer-events-none mix-blend-exclusion"
        aria-hidden="true"
      >
        <Image src={dotsBg} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative z-10 w-[min(141.5rem,100%)] flex flex-col gap-[6rem] lg:gap-[8rem] items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1.125rem] text-center"
        >
          {data?.meansForYouChip && (
            <p className="mb-0 font-blacker italic text-[2rem] text-white/70 leading-[1.5]">
              {data.meansForYouChip}
            </p>
          )}
          {data?.meansForYouTitle && (
            <div className="richtext prose-h2:text-white prose-h2:text-[4rem] lg:prose-h2:text-[6rem] prose-h2:font-medium prose-h2:leading-[1.01] prose-h2:tracking-[-0.3rem]">
              <PortableText
                value={data.meansForYouTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {!!data?.meansForYouStats?.length && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-[3rem] w-full"
          >
            {data.meansForYouStats.map((stat, i) => (
              <div
                key={stat._key ?? i}
                className="backdrop-blur-[2px] bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.17)] rounded-[0.25rem] px-[3rem] py-[3rem] lg:px-[5rem] lg:py-[4.75rem] flex items-center justify-between gap-[2rem] lg:gap-[3rem]"
              >
                <div className="flex flex-col gap-[0.5rem] flex-1">
                  {stat.label && (
                    <p className="mb-0 text-[2.25rem] font-medium text-white/80 leading-[1.4]">
                      {stat.label}
                    </p>
                  )}
                  {stat.description && (
                    <p className="mb-0 text-[2rem] font-normal text-white/50 leading-[1.4]">
                      {stat.description}
                    </p>
                  )}
                </div>
                {stat.value && (
                  <p className="mb-0 text-[5rem] lg:text-[7rem] font-normal text-white tracking-[-0.35rem] leading-[1] flex-shrink-0">
                    {stat.value}
                  </p>
                )}
              </div>
            ))}
          </motion.div>
        )}

        {data?.meansForYouCTA && (
          <motion.div
            className="lg:w-fit w-full"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, ease, delay: 0.15 }}
          >
            <CTAButton data={data.meansForYouCTA} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
